import {useQueries, useQuery} from '@tanstack/react-query'
import {minutesToMilliseconds} from '@/utils/time'
import {getCurrentUserPermissionsKey} from './cache'
import {useCallback, useMemo} from 'react'
import {uniq} from 'lodash'
import {getUserPermissions, ResourceType, UserPermission} from '@sanity/access-api'

function transformPermission(permission: {type: string; action: string}) {
  return `${permission.type}.${permission.action}`
}

export function useCurrentUserScopedPermissions({
  resourceType,
  resourceId,
}: {
  resourceType?: ResourceType
  resourceId?: string
}) {
  return useQuery({
    queryKey: getCurrentUserPermissionsKey(resourceId),
    queryFn: () =>
      getUserPermissions({path: {resourceType: resourceType!, resourceId: resourceId!}}),
    staleTime: minutesToMilliseconds(5),
    select: (data) => data.data.data,
    enabled: !!resourceType && !!resourceId,
  })
}

export function useOrganizationPermissions(organizationId?: string) {
  const {
    data: permissions,
    isLoading,
    error,
  } = useCurrentUserScopedPermissions({
    resourceType: 'organization',
    resourceId: organizationId,
  })

  const projectWildcardPermissions = useMemo(() => {
    return permissions?.filter(
      (grant) => grant.resourceId === '*' && grant.resourceType === 'project'
    )
  }, [permissions])

  const orgPermissions = useMemo(() => {
    return permissions?.filter(
      (grant) => grant.resourceId === organizationId && grant.resourceType === 'organization'
    )
  }, [permissions, organizationId])

  const grants = useMemo(() => {
    return uniq(orgPermissions?.map((grant) => `${grant.type}.${grant.action}`))
  }, [orgPermissions])

  const projectWildcardGrants = useMemo(() => {
    return uniq(projectWildcardPermissions?.map((grant) => `${grant.type}.${grant.action}`))
  }, [projectWildcardPermissions])

  return {permissions: orgPermissions, grants, projectWildcardGrants, isLoading, error}
}

export function useProjectPermissions(projectId?: string, organizationId?: string) {
  const {
    data: permissions = [],
    isLoading,
    error,
  } = useCurrentUserScopedPermissions({
    resourceType: 'project',
    resourceId: projectId,
  })

  const {projectWildcardGrants = []} = useOrganizationPermissions(organizationId)

  const grants = useMemo(() => {
    return uniq([
      ...projectWildcardGrants,
      ...permissions?.map((grant) => `${grant.type}.${grant.action}`),
    ])
  }, [permissions, projectWildcardGrants])

  return {permissions, grants, isLoading, error}
}

export function useResourcesWithPermission({
  resourceIds,
  resourceType,
  permission,
}: {
  resourceIds: string[]
  resourceType: ResourceType
  permission: string
}) {
  const combineQueries = useCallback(
    (results) => {
      return {
        isLoading: results.some((result) => result.isLoading),
        error: results.some((result) => result.error),
        data: resourceIds.filter((resourceId) => {
          return results.some((result) => {
            return result.data?.some((userPermission) => {
              return (
                userPermission.resourceId === resourceId &&
                transformPermission(userPermission) === permission
              )
            })
          })
        }),
      }
    },
    [resourceIds, permission]
  )

  return useQueries({
    queries: resourceIds.map((resourceId) => ({
      queryKey: getCurrentUserPermissionsKey(resourceId),
      queryFn: () =>
        getUserPermissions({path: {resourceType: resourceType!, resourceId: resourceId}}),
      select: (data) => data.data.data as UserPermission[],
      staleTime: minutesToMilliseconds(5),
      enabled: !!resourceId && !!resourceType,
    })),
    combine: combineQueries,
  })
}

export function useOrgsWithPermission({
  orgIds,
  permission,
}: {
  orgIds: string[]
  permission: string
}) {
  return useResourcesWithPermission({
    resourceIds: orgIds,
    resourceType: 'organization',
    permission,
  })
}
